import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@carboledger/ui';
import { ACCOUNT_SECTIONS, ROUTES } from '@/app/routes';
import { DetailPageHeader } from '@/components/common';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { useGetSiteDetailQuery } from '../../api/sitesApi';
import { AssignMaterialsModal } from './AssignMaterialsModal';
import { SiteMassBalancePeriodTabView } from './SiteMassBalancePeriodTabView';
import { SiteMaterialsTabView } from './SiteMaterialsTabView';
import { SiteOverviewTabView } from './SiteOverviewTabView';
import styles from './SiteDetailPage.module.scss';

const DetailTab = Object.freeze({
  overview: 'overview',
  materialSettings: 'materialSettings',
  massBalancePeriod: 'massBalancePeriod',
});

export const SiteDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { siteId } = useParams();
  const [activeTab, setActiveTab] = useState(DetailTab.overview);
  const [isAssigningMaterials, setIsAssigningMaterials] = useState(false);

  const { data: site, isLoading, isError } = useGetSiteDetailQuery(siteId);

  const goToSites = useCallback(() => {
    navigate(ROUTES.accountSetup(ACCOUNT_SECTIONS.companyStructure));
  }, [navigate]);

  const goToAccountSetup = useCallback(() => {
    navigate(ROUTES.accountSetup());
  }, [navigate]);

  const handleEditSite = useCallback(() => {
    navigate(ROUTES.siteEdit(siteId));
  }, [navigate, siteId]);

  const handleAssignMaterials = useCallback(() => setIsAssigningMaterials(true), []);
  const handleCloseAssignMaterials = useCallback(() => setIsAssigningMaterials(false), []);

  const tabs = useMemo(
    () => [
      { value: DetailTab.overview, label: t('SECTION_OVERVIEW') },
      {
        value: DetailTab.materialSettings,
        label: t('SITES_DETAIL_TAB_MATERIAL_SETTINGS'),
      },
      {
        value: DetailTab.massBalancePeriod,
        label: t('SITES_DETAIL_TAB_MASS_BALANCE_PERIOD'),
      },
    ],
    [t],
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: t('ACCOUNT_SETUP_TITLE'), onClick: goToAccountSetup },
      { label: t('ACCOUNT_SETUP_TAB_COMPANY_STRUCTURE'), onClick: goToSites },
      { label: site?.siteName ?? '-' },
    ],
    [t, goToAccountSetup, goToSites, site?.siteName],
  );

  const headerActions = useMemo(
    () => (
      <>
        <Button variant={ButtonVariants.secondary} onClick={handleEditSite}>
          {t('COMMON_EDIT')}
        </Button>
        <Button variant={ButtonVariants.primary} onClick={handleAssignMaterials}>
          {t('SITES_ACTION_ASSIGN_MATERIALS')}
        </Button>
      </>
    ),
    [t, handleEditSite, handleAssignMaterials],
  );

  const metaFields = useMemo(() => {
    if (!site) return [];
    return [
      { id: 'siteId', label: t('FIELD_SITE_ID'), value: site.siteId },
      {
        id: 'siteDeclarationName',
        label: t('FIELD_OFFICIAL_SITE_NAME'),
        value: site.siteDeclarationName,
      },
      { id: 'addressLine1', label: t('FIELD_ADDRESS_LINE_1'), value: site.addressLine1 },
      { id: 'addressLine2', label: t('FIELD_ADDRESS_LINE_2'), value: site.addressLine2 },
      { id: 'city', label: t('FIELD_CITY_NAME'), value: site.city },
      { id: 'country', label: t('FIELD_COUNTRY_NAME'), value: site.country },
      { id: 'zipCode', label: t('FIELD_ZIP_CODE'), value: site.zipCode },
      { id: 'lastUpdated', label: t('FIELD_LAST_UPDATED'), value: site.lastUpdated },
    ];
  }, [t, site]);

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !site) {
    return (
      <EmptyState
        title={t('SITES_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToSites}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={site.siteName}
        actions={headerActions}
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('FIELD_SITE_NAME')}
      />

      <div className={styles.content}>
        {activeTab === DetailTab.overview && <SiteOverviewTabView site={site} />}

        {activeTab === DetailTab.materialSettings && (
          <SiteMaterialsTabView site={site} onAssignMaterials={handleAssignMaterials} />
        )}

        {activeTab === DetailTab.massBalancePeriod && <SiteMassBalancePeriodTabView site={site} />}
      </div>

      <AssignMaterialsModal
        open={isAssigningMaterials}
        onClose={handleCloseAssignMaterials}
        siteId={site.id}
      />
    </div>
  );
};
